import { el, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import {
  pageHeader,
  prose,
  card,
  callout,
  codeBlock,
  rangeControl,
  stat,
  statRow,
  button,
} from "../ui/components";
import { plot, PALETTE } from "../ui/charts";
import { heatGrid } from "../ui/grid";
import { themeColors, h2, legend, caption, ticker } from "./shared";
import { BloomFilter } from "../core/bloom-filter";
import * as sizing from "../core/sizing";
import { formatBytes, formatCount, formatPercent } from "../ui/format";
import type { PageRenderer } from "../ui/router";

export const renderBloom: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Membership · no false negatives",
      title: "Bloom filter",
      lede: "A set that answers “definitely not here” or “probably here” in a handful of bits per item. Add items, watch bits light up, and see the false-positive rate emerge — then size one for your own workload.",
    }),
  );

  append(
    outlet,
    prose(
      el(
        "p",
        {},
        "A Bloom filter is a bit array plus ",
        el("em", {}, "k"),
        " hash functions. To add an item you hash it k ways and set those k bits. To test an item you check the same k bits: if any is 0 it was ",
        el("strong", {}, "definitely never added"),
        "; if all are 1 it's ",
        el("strong", {}, "probably present"),
        " — probably, because those bits might have been set by other items. That's the only error mode: false positives, never false negatives.",
      ),
    ),
  );

  demoCard(outlet);
  calculatorCard(outlet);
  measurementCard(outlet);

  append(outlet, h2("Use it"));
  append(
    outlet,
    codeBlock(
      `import { BloomFilter } from "./core";

// Size for 1M items at a 1% false-positive rate.
const bf = BloomFilter.optimal(1_000_000, 0.01);
bf.add("alice@example.com");

bf.has("alice@example.com"); // → true  (probably)
bf.has("nobody@example.com"); // → false (definitely) — or a rare false positive`,
    ),
  );

  append(
    outlet,
    callout(
      "warn",
      el(
        "div",
        {},
        el("strong", {}, "Trade-offs. "),
        "A classic Bloom filter can't delete items or count them, and it can't tell you which items it holds. If you need deletion, reach for the ",
        el("a", { href: "#/cuckoo" }, "Cuckoo filter"),
        ". As it fills past its design capacity, the false-positive rate climbs toward 1.",
      ),
    ),
  );
};

// ---------------------------------------------------------------------------
// Demo: a small, live filter you can add to and probe.
// ---------------------------------------------------------------------------

function demoCard(outlet: HTMLElement): void {
  const BITS = 256;
  const kSig = signal(4);
  const testWord = signal("");
  const { tick, bump } = ticker(); // advanced on any data change
  const added = new Set<string>();
  let bf = new BloomFilter({ bits: BITS, hashes: kSig.value });

  function rebuild(): void {
    bf = new BloomFilter({ bits: BITS, hashes: kSig.value });
    for (const w of added) bf.add(w);
  }

  // Only this effect depends on k; it rebuilds, then bumps so the draw/stat
  // effects (which depend on `tick`) refresh against the new filter.
  effect(() => {
    kSig.value;
    rebuild();
    bump();
  });

  const grid = heatGrid(32, 8, { cellSize: 15, gap: 3 });
  const chips = el("div", {});
  const result = el("div", { class: "mini-note", style: { minHeight: "1.4em", marginTop: "8px" } });

  function addWord(w: string): void {
    w = w.trim();
    if (!w) return;
    added.add(w);
    bf.add(w);
    bump();
    renderChips();
  }

  function renderChips(): void {
    chips.replaceChildren(
      ...[...added].slice(-24).map((w) => el("span", { class: "chip" }, w)),
    );
  }

  const wordInput = el("input", {
    class: "textinput",
    type: "text",
    placeholder: "add a word, e.g. banana",
    onkeydown: (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        addWord((e.target as HTMLInputElement).value);
        (e.target as HTMLInputElement).value = "";
      }
    },
  }) as HTMLInputElement;

  const testInput = el("input", {
    class: "textinput",
    type: "text",
    placeholder: "test any word…",
    oninput: (e: Event) => (testWord.value = (e.target as HTMLInputElement).value),
  }) as HTMLInputElement;

  // Redraw grid + result whenever the filter or the tested word changes.
  effect(() => {
    tick.value;
    const c = themeColors();
    const probes = testWord.value ? new Set(bf.probeIndices(testWord.value)) : new Set<number>();
    grid.draw((i) => {
      const set = bf.bits.get(i);
      if (probes.has(i)) return set ? c.accent2 : "#f472b6";
      return set ? c.accent : c.border;
    });

    const w = testWord.value.trim();
    if (!w) {
      result.textContent = "";
      return;
    }
    const present = bf.has(w);
    const truly = added.has(w);
    if (!present) result.innerHTML = `<span style="color:var(--text-dim)">“${escapeHtml(w)}” → <b>definitely not added</b> (some probed bit is 0)</span>`;
    else if (truly) result.innerHTML = `<span style="color:var(--accent)">“${escapeHtml(w)}” → <b>present</b> ✓ (you added it)</span>`;
    else result.innerHTML = `<span style="color:var(--warn)">“${escapeHtml(w)}” → <b>false positive!</b> all k bits happen to be set</span>`;
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    el("label", { class: "control" }, el("span", { class: "control-label" }, "Add words"), wordInput),
    el("label", { class: "control" }, el("span", { class: "control-label" }, "Test membership"), testInput),
    rangeControl({ label: "hash functions k", min: 1, max: 8, signal: kSig, format: (n) => `${n}` }),
    button({
      label: "Add 12 random words",
      variant: "ghost",
      onClick: () => {
        for (let i = 0; i < 12; i++) addWord(randomWord());
      },
    }),
    button({
      label: "Clear",
      variant: "ghost",
      onClick: () => {
        added.clear();
        rebuild();
        bump();
        renderChips();
      },
    }),
    statRow(
      stat({ label: "Words added", value: () => (tick.value, String(added.size)) }),
      stat({
        label: "Bits set",
        value: () => (tick.value, `${bf.bits.popcount()} / ${BITS}`),
      }),
      stat({
        label: "Fill",
        accent: true,
        value: () => (tick.value, formatPercent(bf.fillRatio())),
      }),
    ),
  );

  const viz = el(
    "div",
    {},
    el("div", { class: "viz-wrap" }, grid.canvas),
    legend([
      [themeColors().accent, "bit set"],
      [themeColors().border, "bit unset"],
      [themeColors().accent2, "probed & set"],
      ["#f472b6", "probed & unset"],
    ]),
    result,
    el("div", { style: { marginTop: "10px" } }, chips),
    caption("256-bit filter. Type in the test box to highlight the k bits a word probes."),
  );

  append(
    outlet,
    card({
      title: "Watch the bits",
      subtitle: "Add words, then test membership. A word is 'present' only if every one of its k bits is set.",
      children: [el("div", { class: "lab-layout" }, controls, viz)],
    }),
  );
}

// ---------------------------------------------------------------------------
// Calculator: size a filter for n items and a target FPR.
// ---------------------------------------------------------------------------

function calculatorCard(outlet: HTMLElement): void {
  const nExp = signal(6); // n = 10^nExp
  const pExp = signal(2); // p = 10^-pExp

  const nOf = () => Math.round(10 ** nExp.value);
  const pOf = () => 10 ** -pExp.value;

  const chartWrap = el("div", { class: "viz-wrap" });

  effect(() => {
    const n = nOf();
    const p = pOf();
    // Memory vs target FPR for this n (log-log-ish).
    const points: Array<[number, number]> = [];
    for (let e = 0.3; e <= 6.01; e += 0.15) {
      const pp = 10 ** -e;
      const m = sizing.bloomBits(n, pp);
      points.push([pp, sizing.bloomMemoryBytes(m)]);
    }
    const m = sizing.bloomBits(n, p);
    const cur: Array<[number, number]> = [[p, sizing.bloomMemoryBytes(m)]];
    chartWrap.replaceChildren(
      plot({
        height: 300,
        xScale: "log",
        yScale: "log",
        xLabel: "target false-positive rate",
        yLabel: "memory (bytes)",
        xFormat: (v) => formatPercent(v),
        yFormat: (v) => formatBytes(v),
        series: [
          { type: "line", points, color: PALETTE[1], label: `n = ${formatCount(n)}` },
          { type: "scatter", points: cur, color: PALETTE[0], radius: 5 },
        ],
        legend: true,
      }),
    );
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    rangeControl({
      label: "expected items n",
      min: 2,
      max: 9,
      step: 0.1,
      signal: nExp,
      format: () => formatCount(nOf()),
    }),
    rangeControl({
      label: "target false-positive rate",
      min: 0.3,
      max: 6,
      step: 0.1,
      signal: pExp,
      format: () => formatPercent(pOf()),
    }),
    statRow(
      stat({ label: "Bits (m)", value: () => formatCount(sizing.bloomBits(nOf(), pOf())) }),
      stat({ label: "Hashes (k)", value: () => String(sizing.bloomHashes(sizing.bloomBits(nOf(), pOf()), nOf())) }),
    ),
    statRow(
      stat({
        label: "Memory",
        accent: true,
        value: () => formatBytes(sizing.bloomMemoryBytes(sizing.bloomBits(nOf(), pOf()))),
      }),
      stat({
        label: "Bits / item",
        value: () => (sizing.bloomBits(nOf(), pOf()) / nOf()).toFixed(1),
      }),
    ),
    stat({
      label: "vs. an exact hash set",
      value: () =>
        `${(sizing.exactSetMemoryBytes(nOf()) / sizing.bloomMemoryBytes(sizing.bloomBits(nOf(), pOf()))).toFixed(0)}× smaller`,
      hint: "assuming ~48 bytes per entry for an exact set",
    }),
  );

  append(
    outlet,
    card({
      title: "Size one for your workload",
      subtitle: "The optimal bit count is m = −n·ln(p) / (ln 2)², with k = (m/n)·ln 2 hash functions.",
      children: [el("div", { class: "lab-layout" }, controls, chartWrap)],
    }),
  );
}

// ---------------------------------------------------------------------------
// Measurement: prove the theory by inserting items and counting false positives.
// ---------------------------------------------------------------------------

function measurementCard(outlet: HTMLElement): void {
  const bpiSig = signal(10); // bits per item
  const N = 5000;
  const TRIALS = 40000;
  const measured: Array<[number, number]> = [];
  const status = signal(0);

  function theoreticalCurve(): Array<[number, number]> {
    const pts: Array<[number, number]> = [];
    for (let bpi = 4; bpi <= 20.01; bpi += 0.5) {
      const m = Math.round(bpi * N);
      const k = sizing.bloomHashes(m, N);
      pts.push([bpi, sizing.bloomFalsePositiveRate(m, N, k)]);
    }
    return pts;
  }

  const chartWrap = el("div", { class: "viz-wrap" });

  function runMeasurement(): void {
    const bpi = bpiSig.value;
    const m = Math.round(bpi * N);
    const k = sizing.bloomHashes(m, N);
    const bf = new BloomFilter({ bits: m, hashes: k });
    for (let i = 0; i < N; i++) bf.add(`member-${i}`);
    let fp = 0;
    for (let i = 0; i < TRIALS; i++) if (bf.has(`stranger-${i}#`)) fp++;
    measured.push([bpi, fp / TRIALS]);
    status.value++;
  }

  effect(() => {
    status.value;
    chartWrap.replaceChildren(
      plot({
        height: 300,
        yScale: "log",
        xLabel: "bits per item (m / n)",
        yLabel: "false-positive rate",
        yFormat: (v) => formatPercent(v),
        xFormat: (v) => String(v),
        series: [
          { type: "line", points: theoreticalCurve(), color: PALETTE[1], label: "theoretical" },
          {
            type: "scatter",
            points: measured,
            color: PALETTE[0],
            radius: 4.5,
            label: "measured",
          },
        ],
        legend: true,
      }),
    );
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    rangeControl({
      label: "bits per item",
      min: 4,
      max: 20,
      step: 1,
      signal: bpiSig,
      format: (n) => `${n}`,
    }),
    button({ label: `Insert ${formatCount(N)} items & measure`, variant: "primary", onClick: runMeasurement }),
    button({
      label: "Clear points",
      variant: "ghost",
      onClick: () => {
        measured.length = 0;
        status.value++;
      },
    }),
    stat({
      label: "Latest measured FPR",
      accent: true,
      value: () => (status.value, measured.length ? formatPercent(measured[measured.length - 1][1]) : "—"),
    }),
    el(
      "p",
      { class: "mini-note" },
      "Each click builds a real filter, inserts 5,000 items, then tests 40,000 strangers. The dots should land on the curve.",
    ),
  );

  append(
    outlet,
    card({
      title: "Theory vs. reality",
      subtitle: "More bits per item means exponentially fewer false positives. Run a few measurements and watch them fall on the predicted line.",
      children: [el("div", { class: "lab-layout" }, controls, chartWrap)],
    }),
  );
}

// ---------------------------------------------------------------------------

const WORDS = [
  "banana", "orange", "guava", "lychee", "cherry", "melon", "papaya", "quince",
  "apricot", "durian", "fig", "kiwi", "mango", "nectarine", "plum", "raisin",
];
function randomWord(): string {
  const w = WORDS[Math.floor(Math.random() * WORDS.length)];
  return `${w}-${Math.floor(Math.random() * 100000)}`;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}
